// User Registry Management
// Handles username validation and user lookup in Firestore

import { getFirestore, doc, setDoc, getDoc, deleteDoc, collection, query, where, getDocs, runTransaction } from "firebase/firestore";

export const UserRegistry = {
  // Check if username is available (or if current user can reclaim it)
  checkUsernameAvailable: async (username, db, currentUserId = null, currentPhoneNumber = null) => {
    if (!username || !db) return false;
    
    const cleanUsername = username.trim().toLowerCase().replace('@', '');
    
    // Username validation rules
    if (cleanUsername.length < 3 || cleanUsername.length > 20) {
      throw new Error('Username must be 3-20 characters long');
    }
    
    if (!/^[a-z0-9_-]+$/.test(cleanUsername)) {
      throw new Error('Username can only contain letters, numbers, underscores, and hyphens');
    }
    
    if (cleanUsername.startsWith('_') || cleanUsername.endsWith('_') || cleanUsername.startsWith('-') || cleanUsername.endsWith('-')) {
      throw new Error('Username cannot start or end with underscore or hyphen');
    }
    
    try {
      const userDocRef = doc(db, 'users', cleanUsername);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        return true; // Username is available
      }
      
      // Username exists, check if current user can reclaim it
      const existingUserData = userDoc.data();

      // Helper to strip phone to digits only for comparison
      const stripPhone = (p) => p ? p.replace(/\D/g, '') : '';

      // Log for debugging ownership checks
      console.log('Username ownership check:', {
        username: cleanUsername,
        docUserId: existingUserData.userId,
        currentUserId,
        docPhone: existingUserData.phoneNumber,
        currentPhone: currentPhoneNumber
      });

      // Allow reclaiming if:
      // 1. Same userId
      if (currentUserId && existingUserData.userId === currentUserId) {
        console.log('Username reclaim: matched by userId');
        return true;
      }

      // 2. Same phone number (normalize both — strip to digits for comparison)
      if (currentPhoneNumber && existingUserData.phoneNumber) {
        if (stripPhone(currentPhoneNumber) === stripPhone(existingUserData.phoneNumber)) {
          console.log('Username reclaim: matched by phone number');
          return true;
        }
      }

      // 3. Match via phone_ UID pattern (e.g., userId "phone_11234567890" matches phone "+11234567890")
      if (currentUserId && currentUserId.startsWith('phone_') && existingUserData.phoneNumber) {
        const phoneFromUid = currentUserId.replace('phone_', '');
        const phoneFromDoc = stripPhone(existingUserData.phoneNumber);
        if (phoneFromUid === phoneFromDoc) {
          console.log('Username reclaim: matched by UID-phone pattern');
          return true;
        }
      }

      // 4. Check if current user already owns this username via reverse lookup
      //    (handles cases where the doc has an old userId from before migration)
      if (currentUserId) {
        try {
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where('userId', '==', currentUserId));
          const snapshot = await getDocs(q);
          for (const existingDoc of snapshot.docs) {
            if (existingDoc.id === cleanUsername) {
              console.log('Username reclaim: matched by reverse lookup');
              return true;
            }
          }
          // Also check if user has a migration mapping from old UID
          const migrationDoc = await getDoc(doc(db, 'user_migrations', currentUserId));
          if (migrationDoc.exists()) {
            const { oldUid } = migrationDoc.data();
            if (oldUid && existingUserData.userId === oldUid) {
              console.log('Username reclaim: matched by migration mapping (old UID:', oldUid, ')');
              return true;
            }
          }
        } catch (lookupError) {
          console.error('Error in reverse username lookup:', lookupError);
        }
      }

      console.log('Username check: NOT owned by current user');
      return false; // Username taken by someone else
    } catch (error) {
      console.error('Error checking username availability:', error);
      return false;
    }
  },

  // Reserve a username for a user (atomic operation)
  reserveUsername: async (username, userId, userProfile, db) => {
    if (!username || !userId || !db) {
      throw new Error('Missing required parameters');
    }
    
    const cleanUsername = username.trim().toLowerCase().replace('@', '');
    
    try {
      return await runTransaction(db, async (transaction) => {
        const userDocRef = doc(db, 'users', cleanUsername);
        const userDoc = await transaction.get(userDocRef);
        
        if (userDoc.exists()) {
          const existingUserData = userDoc.data();
          
          // Check if current user can reclaim this username
          const stripPhone = (p) => p ? p.replace(/\D/g, '') : '';
          const phonesMatch = userProfile?.phoneNumber && existingUserData.phoneNumber &&
            stripPhone(userProfile.phoneNumber) === stripPhone(existingUserData.phoneNumber);
          const uidMatchesPhone = userId && userId.startsWith('phone_') && existingUserData.phoneNumber &&
            userId.replace('phone_', '') === stripPhone(existingUserData.phoneNumber);

          // Check migration mapping for old UID match
          let migrationMatch = false;
          if (userId && existingUserData.userId !== userId) {
            try {
              const migDoc = await getDoc(doc(db, 'user_migrations', userId));
              if (migDoc.exists() && migDoc.data().oldUid === existingUserData.userId) {
                migrationMatch = true;
              }
            } catch (e) { /* ignore */ }
          }

          const canReclaim = (existingUserData.userId === userId) || phonesMatch || uidMatchesPhone || migrationMatch;
          
          if (!canReclaim) {
            throw new Error('Username is already taken');
          }
          
          // User can reclaim - update existing record
          console.log('User reclaiming username:', cleanUsername);
        }
        
        // Create user document with username as ID
        // Note: Exclude profilePicture from Firestore due to size limits
        const userData = {
          userId: userId,
          username: cleanUsername,
          displayName: userProfile?.displayName || '',
          phoneNumber: userProfile?.phoneNumber || null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        transaction.set(userDocRef, userData);
        return true;
      });
    } catch (error) {
      console.error('Error reserving username:', error);
      throw error;
    }
  },

  // Update user data in registry
  updateUserInRegistry: async (username, updates, db) => {
    if (!username || !db) return false;
    
    const cleanUsername = username.trim().toLowerCase().replace('@', '');
    
    try {
      const userDocRef = doc(db, 'users', cleanUsername);
      const updateData = {
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      await setDoc(userDocRef, updateData, { merge: true });
      return true;
    } catch (error) {
      console.error('Error updating user in registry:', error);
      return false;
    }
  },

  // Find user by username
  findUserByUsername: async (username, db) => {
    console.log('findUserByUsername called with:', { username, db: !!db });
    if (!username || !db) {
      console.log('findUserByUsername: missing params');
      return null;
    }
    
    const cleanUsername = username.trim().toLowerCase().replace('@', '');
    console.log('findUserByUsername: searching for cleaned username:', cleanUsername);
    
    try {
      const userDocRef = doc(db, 'users', cleanUsername);
      console.log('findUserByUsername: doc ref created');
      const userDoc = await getDoc(userDocRef);
      console.log('findUserByUsername: doc retrieved, exists:', userDoc.exists());
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('findUserByUsername: user data found:', userData);
        
        // Also try to get profile picture from the correct path
        try {
          const profileDocRef = doc(db, `users/${userData.userId}/profile/data`);
          const profileDoc = await getDoc(profileDocRef);
          
          if (profileDoc.exists()) {
            const profileData = profileDoc.data();
            userData.profilePicture = profileData.profilePicture;
          }
        } catch (profileError) {
          console.log('Could not fetch profile picture:', profileError);
        }
        
        return userData;
      }
      console.log('findUserByUsername: no user found');
      return null;
    } catch (error) {
      console.error('Error finding user by username:', error);
      return null;
    }
  },

  // Find user by phone number
  findUserByPhone: async (phoneNumber, db) => {
    if (!phoneNumber || !db) return null;
    
    try {
      // Search users collection by phone number
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('phoneNumber', '==', phoneNumber));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        
        // Also try to get profile picture from the correct path
        try {
          const profileDocRef = doc(db, `users/${userData.userId}/profile/data`);
          const profileDoc = await getDoc(profileDocRef);
          
          if (profileDoc.exists()) {
            const profileData = profileDoc.data();
            userData.profilePicture = profileData.profilePicture;
          }
        } catch (profileError) {
          console.log('Could not fetch profile picture:', profileError);
        }
        
        return userData;
      }
      return null;
    } catch (error) {
      console.error('Error finding user by phone:', error);
      return null;
    }
  },

  // Get current user's username from registry
  getCurrentUserUsername: async (userId, db) => {
    if (!userId || !db) return null;
    
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        return querySnapshot.docs[0].data().username;
      }
      return null;
    } catch (error) {
      console.error('Error getting current user username:', error);
      return null;
    }
  },

  // Generate a fun username fallback
  generateUsername: () => {
    const adjectives = ['crispy', 'spicy', 'sweet', 'fresh', 'zesty', 'smoky', 'tangy', 'savory', 'juicy', 'crunchy'];
    const foods = ['banana', 'taco', 'pizza', 'cookie', 'mango', 'waffle', 'pretzel', 'bagel', 'pickle', 'donut'];
    
    const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const food = foods[Math.floor(Math.random() * foods.length)];
    const number = Math.floor(Math.random() * 999) + 1;
    
    return `${adjective}_${food}_${number}`;
  },

  // Create user record on signup with auto-generated username
  createUserRecord: async (userId, phoneNumber, displayName, db, venmoUsername = null) => {
    console.log('createUserRecord called with:', { userId, phoneNumber, displayName, venmoUsername, db: !!db });
    if (!userId || !phoneNumber || !db) {
      console.log('createUserRecord: missing required params');
      return false;
    }
    
    try {
      // First try Venmo username, then generate fun username
      let autoUsername = venmoUsername;
      if (!autoUsername) {
        autoUsername = UserRegistry.generateUsername();
        console.log('createUserRecord: generated username:', autoUsername);

        // Ensure generated username is unique
        let attempts = 0;
        while (attempts < 10) {
          const isAvailable = await UserRegistry.checkUsernameAvailable(autoUsername, db, userId, phoneNumber);
          console.log('createUserRecord: username available check:', { username: autoUsername, available: isAvailable });
          if (isAvailable) break;
          autoUsername = UserRegistry.generateUsername();
          attempts++;
        }
      } else {
        // Check if Venmo username is available, if not add numbers
        let attempts = 0;
        let baseUsername = autoUsername;
        while (attempts < 10) {
          const isAvailable = await UserRegistry.checkUsernameAvailable(autoUsername, db, userId, phoneNumber);
          console.log('createUserRecord: venmo username available check:', { username: autoUsername, available: isAvailable });
          if (isAvailable) break;
          attempts++;
          autoUsername = `${baseUsername}_${attempts}`;
        }
      }
      
      console.log('createUserRecord: final username to reserve:', autoUsername);
      // Reserve the username in the users collection
      await UserRegistry.reserveUsername(autoUsername, userId, { displayName, phoneNumber }, db);
      console.log('createUserRecord: username reserved successfully');
      
      return autoUsername;
    } catch (error) {
      console.error('Error creating user record:', error);
      return false;
    }
  },

  // Remove username reservation (for cleanup)
  removeUsername: async (username, db) => {
    if (!username || !db) return false;
    
    const cleanUsername = username.trim().toLowerCase().replace('@', '');
    
    try {
      const userDocRef = doc(db, 'users', cleanUsername);
      await deleteDoc(userDocRef);
      return true;
    } catch (error) {
      console.error('Error removing username:', error);
      return false;
    }
  },

  // Migrate existing user with username from localStorage to Firestore
  migrateExistingUser: async (userId, userProfile, db) => {
    if (!userId || !userProfile || !db) return false;
    
    // Only migrate if user has a username but isn't in Firestore yet
    if (!userProfile.username) return false;
    
    try {
      // Check if user already exists in Firestore
      const existingUser = await UserRegistry.findUserByUsername(userProfile.username, db);
      if (existingUser) {
        return true; // User already migrated
      }
      
      // Migrate user to Firestore
      await UserRegistry.reserveUsername(
        userProfile.username, 
        userId, 
        {
          displayName: userProfile.displayName || '',
          phoneNumber: userProfile.phoneNumber || null
        }, 
        db
      );
      
      return true;
    } catch (error) {
      console.error('Error migrating existing user:', error);
      return false;
    }
  },

  // Migrate user data from old UID to new UID (for Firebase Phone Auth -> Twilio migration)
  migrateUserId: async (oldUid, newUid, db) => {
    if (!oldUid || !newUid || !db || oldUid === newUid) return false;

    try {
      console.log('Migrating user data from', oldUid, 'to', newUid);

      // Find user documents that reference the old UID
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('userId', '==', oldUid));
      const snapshot = await getDocs(q);

      let migrated = 0;

      for (const userDoc of snapshot.docs) {
        try {
          const userData = userDoc.data();
          // Update the userId field to the new UID
          await setDoc(doc(db, 'users', userDoc.id), {
            ...userData,
            userId: newUid,
            previousUserId: oldUid,
            migratedAt: new Date().toISOString()
          });
          migrated++;
          console.log('Migrated user doc:', userDoc.id, 'from', oldUid, 'to', newUid);
        } catch (docError) {
          console.error('Error migrating doc:', userDoc.id, docError);
        }
      }

      // Copy Firestore profile data from old UID to new UID
      try {
        const oldProfileDoc = await getDoc(doc(db, `users/${oldUid}/profile/data`));
        if (oldProfileDoc.exists()) {
          const oldProfileData = oldProfileDoc.data();
          // Check if new profile already has data (don't overwrite)
          const newProfileDoc = await getDoc(doc(db, `users/${newUid}/profile/data`));
          const newProfileData = newProfileDoc.exists() ? newProfileDoc.data() : {};

          await setDoc(doc(db, `users/${newUid}/profile/data`), {
            ...oldProfileData,
            ...newProfileData, // New data takes priority if it exists
            migratedFrom: oldUid,
            migratedAt: new Date().toISOString()
          });
          console.log('Migrated Firestore profile from', oldUid, 'to', newUid);
        }
      } catch (profileError) {
        console.error('Error migrating Firestore profile:', profileError);
      }

      console.log(`Migration complete: ${migrated} documents updated`);
      return true;
    } catch (error) {
      console.error('Error in migrateUserId:', error);
      return false;
    }
  }
};